import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLButtonElement} addAnotherButton - Repeat-add button revealed after a successful add
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #cartRevealTimeout = null;

  /** @type {number | null} */
  #shippingCelebrationTimeout = null;

  /** @type {number | null} */
  #stickySubmitTimeout = null;

  /** @type {HTMLButtonElement | null} */
  #pendingSubmitButton = null;

  /** @type {boolean} */
  #stickyAddPending = false;

  /** @type {number} */
  #cartSyncRequest = 0;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {MutationObserver | null} */
  #floatingWidgetsObserver = null;

  /** @type {MutationObserver | null} */
  #verifastStateObserver = null;

  /** @type {HTMLElement | null} */
  #observedVerifastFrame = null;

  /** @type {number | null} */
  #floatingWidgetsScanFrame = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #activeStickySelector = null;

  /** @type {HTMLElement | null} */
  #stickySheet = null;

  /** @type {HTMLElement | null} */
  #previousFocus = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#cacheTargetAddToCartButton();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    document.addEventListener('keydown', this.#handleDocumentKeydown, { signal });
    window.addEventListener('resize', this.#handleViewportChange, { signal });
    this.addEventListener('change', this.#handleStickyOptionChange, { signal });
    this.addEventListener('click', this.#handleStickySelectorClick, { signal });

    this.#getInitialQuantity();
    this.#setupFloatingWidgetsObserver();
    this.#handleViewportChange();
    this.#updateShippingProgress(Number.parseInt(this.dataset.cartTotal || '0', 10) || 0, false);
    this.#setupStickyHeightObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#stickyResizeObserver?.disconnect();
    this.#floatingWidgetsObserver?.disconnect();
    this.#verifastStateObserver?.disconnect();
    if (this.#floatingWidgetsScanFrame) cancelAnimationFrame(this.#floatingWidgetsScanFrame);
    this.#closeStickySheet(false);
    this.#abortController.abort();
    this.#setFloatingWidgetsLift(false);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#cartRevealTimeout) {
      clearTimeout(this.#cartRevealTimeout);
    }
    if (this.#shippingCelebrationTimeout) {
      clearTimeout(this.#shippingCelebrationTimeout);
    }
    if (this.#stickySubmitTimeout) {
      clearTimeout(this.#stickySubmitTimeout);
    }
    document.body.style.removeProperty('--veda-sticky-atc-mobile-height');
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      if (this.#shouldAlwaysStickOnMobile()) {
        this.#hiddenByBottom = false;
        this.#showStickyBar();
        return;
      }

      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        if (this.#shouldAlwaysStickOnMobile()) {
          this.#hiddenByBottom = false;
          this.#showStickyBar();
          return;
        }

        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#cacheTargetAddToCartButton();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    const stickyButton = this.refs.addToCartButton;

    if (stickyButton.dataset.action === 'view-cart') {
      this.#openCart();
      return;
    }

    this.#submitProduct(stickyButton);
  };

  /**
   * Adds one more of the selected variant while keeping View cart as the primary action.
   */
  handleAddAnotherClick = () => {
    const addAnotherButton = this.refs.addAnotherButton;
    if (!(addAnotherButton instanceof HTMLButtonElement)) return;
    this.#submitProduct(addAnotherButton);
  };

  /**
   * Submits through the canonical product form so app and inventory integrations stay intact.
   * @param {HTMLButtonElement} triggerButton
   */
  #submitProduct(triggerButton) {
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled || this.#stickyAddPending) return;

    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form');
    if (!(form instanceof HTMLFormElement) || !form.checkValidity()) {
      form?.reportValidity();
      return;
    }

    this.#stickyAddPending = true;
    this.#pendingSubmitButton = triggerButton;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    triggerButton.dataset.loading = 'true';
    triggerButton.setAttribute('aria-busy', 'true');
    this.#targetAddToCartButton.click();

    if (this.#stickySubmitTimeout) clearTimeout(this.#stickySubmitTimeout);
    this.#stickySubmitTimeout = window.setTimeout(() => {
      this.#resetStickySubmission();
    }, 12000);
  }

  /**
   * Scrolls back to the product media gallery when the sticky product image is clicked.
   */
  scrollToMedia = () => {
    const sectionElement = this.closest('.shopify-section');
    const media =
      sectionElement?.querySelector('[data-testid="product-information-media"]') ||
      document.querySelector('[data-testid="product-information-media"]');

    if (!(media instanceof HTMLElement)) return;

    media.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  /**
   * Moves the customer to the main size selector without opening the sticky variant sheet.
   */
  scrollToSizeSelector = () => {
    const sectionElement = this.closest('.shopify-section');
    const variantPicker = sectionElement?.querySelector('variant-picker');
    const sizeSelector =
      variantPicker?.querySelector('[data-veda-size-selector="true"]') ||
      variantPicker?.querySelector('.variant-option--size') ||
      variantPicker;

    if (!(sizeSelector instanceof HTMLElement)) return;

    sizeSelector.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });

    sizeSelector.classList.remove('veda-size-selector-focus');
    requestAnimationFrame(() => sizeSelector.classList.add('veda-size-selector-focus'));
    window.setTimeout(() => sizeSelector.classList.remove('veda-size-selector-focus'), 900);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('veda-pdp-sticky');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    this.#closeStickySheet(false);

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#cacheTargetAddToCartButton();
    }
    this.#setCartState(Number.parseInt(this.dataset.cartCount || '0', 10) || 0);
    this.#resetStickySubmission();
    this.#setButtonAction('add-to-cart');
    this.#updateShippingProgress(Number.parseInt(this.dataset.cartTotal || '0', 10) || 0, false);
    this.#syncStickyBarHeight();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.veda-pdp-sticky__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles successful cart updates and reveals the cart shortcut.
   * @param {CustomEvent} event - The cart event
   */
  #handleCartUpdate = (event) => {
    const isStickySubmission = this.#stickyAddPending && event.detail?.data?.source === 'product-form-component';

    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (event.detail?.data?.didError) {
      if (isStickySubmission) {
        this.#resetStickySubmission();
      }
      return;
    }

    const currentCount = Number.parseInt(this.dataset.cartCount || '0', 10) || 0;
    const resourceCount = Number.parseInt(String(event.detail?.resource?.item_count ?? ''), 10);
    const eventCount = Number.parseInt(String(event.detail?.data?.itemCount ?? ''), 10);
    const fromProductForm = event.detail?.data?.source === 'product-form-component';

    let nextCount = currentCount;
    if (Number.isFinite(resourceCount)) {
      nextCount = resourceCount;
    } else if (Number.isFinite(eventCount)) {
      nextCount = fromProductForm ? currentCount + eventCount : eventCount;
    } else if (this.dataset.cartHasItems !== 'true') {
      nextCount = 1;
    }

    this.#setCartState(Math.max(nextCount, 1), true);
    if (isStickySubmission) this.#completeStickySubmission();
    void this.#syncCartSnapshot(event.detail?.resource, true);
  };

  /**
   * Handles cart errors - only resets the puppet flag.
   */
  #handleCartError = () => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    if (this.#stickyAddPending) {
      this.#resetStickySubmission();
    }
  };

  /**
   * Converts a confirmed sticky add into an explicit cart action.
   */
  #completeStickySubmission() {
    this.#resetStickySubmission();
    this.#setButtonAction('view-cart');
    void this.#animateStickyAddSuccess();
  }

  /**
   * Clears request-only sticky button state without changing its action.
   */
  #resetStickySubmission() {
    this.#stickyAddPending = false;
    if (this.#stickySubmitTimeout) {
      clearTimeout(this.#stickySubmitTimeout);
      this.#stickySubmitTimeout = null;
    }

    const productForm = this.#getProductForm();
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';

    const buttons = [this.refs.addToCartButton, this.refs.addAnotherButton, this.#pendingSubmitButton];
    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      delete button.dataset.loading;
      button.removeAttribute('aria-busy');
    });
    this.#pendingSubmitButton = null;
  }

  /**
   * Updates the sticky button label and icon without replacing the product form.
   * @param {'add-to-cart' | 'view-cart'} action
   */
  #setButtonAction(action) {
    const button = this.refs.addToCartButton;
    const label = button.querySelector('[data-sticky-button-label]');
    const isViewCart = action === 'view-cart';

    button.dataset.action = action;
    this.dataset.buttonAction = action;
    button.disabled = isViewCart ? false : this.dataset.variantAvailable !== 'true';
    if (label) {
      label.textContent = isViewCart ? button.dataset.viewLabel || 'View cart' : button.dataset.addLabel || 'Add to cart';
    }
  }

  /**
   * Opens the existing GoKwik/native cart only after an explicit View Cart click.
   */
  #openCart() {
    if (typeof window.__openVedaCart === 'function') {
      window.__openVedaCart();
      return;
    }

    const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"], #cart-icon-bubble');
    if (cartTrigger instanceof HTMLElement) {
      cartTrigger.click();
      return;
    }

    window.location.assign(this.dataset.cartUrl || '/cart');
  }

  /**
   * Plays the existing confirmation and fly-to-cart animation after success.
   */
  async #animateStickyAddSuccess() {
    const stickyButton = this.refs.addToCartButton;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const animatedElements = [stickyButton];
    stickyButton.dataset.added = 'true';

    if (cartIcon && this.refs.productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    await onAnimationEnd(animatedElements);
    this.#resetTimeout = window.setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 500);
  }

  /**
   * Updates sticky cart shortcut state.
   * @param {number} count
   * @param {boolean} justAdded
   */
  #setCartState(count, justAdded = false) {
    const safeCount = Math.max(0, count);
    this.dataset.cartCount = String(safeCount);
    this.dataset.cartHasItems = safeCount > 0 ? 'true' : 'false';

    const countElement = this.querySelector('.veda-pdp-sticky__cart-count');
    if (countElement) {
      countElement.textContent = safeCount > 99 ? '99+' : String(safeCount);
    }

    if (!justAdded) return;

    this.dataset.cartJustAdded = 'true';
    if (this.#cartRevealTimeout) clearTimeout(this.#cartRevealTimeout);
    this.#cartRevealTimeout = window.setTimeout(() => {
      delete this.dataset.cartJustAdded;
    }, 1800);
  };

  /**
   * Uses a cart response when available and falls back to Shopify's cart JSON endpoint.
   * @param {Object | undefined} resource
   * @param {boolean} celebrate
   */
  async #syncCartSnapshot(resource, celebrate) {
    const resourceTotal = Number.parseInt(String(resource?.total_price ?? ''), 10);
    if (Number.isFinite(resourceTotal)) {
      const resourceCount = Number.parseInt(String(resource?.item_count ?? ''), 10);
      if (Number.isFinite(resourceCount)) this.#setCartState(resourceCount, false);
      this.#updateShippingProgress(resourceTotal, celebrate);
      return;
    }

    const requestId = ++this.#cartSyncRequest;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (requestId !== this.#cartSyncRequest) return;

      const cartTotal = Number.parseInt(String(cart?.total_price ?? '0'), 10) || 0;
      const cartCount = Number.parseInt(String(cart?.item_count ?? '0'), 10) || 0;
      this.#setCartState(cartCount, false);
      this.#updateShippingProgress(cartTotal, celebrate);
    } catch (error) {
      console.warn('Unable to refresh free shipping progress.', error);
    }
  }

  /**
   * Updates the free-shipping message, progress bar, and restrained unlock animation.
   * @param {number} cartTotal
   * @param {boolean} celebrate
   */
  #updateShippingProgress(cartTotal, celebrate) {
    if (this.dataset.freeShippingEnabled !== 'true') return;

    const threshold = Number.parseInt(this.dataset.freeShippingThreshold || '0', 10) || 0;
    const offer = this.querySelector('[data-shipping-progress]');
    const message = offer?.querySelector('[data-shipping-message]');
    const progressTrack = offer?.querySelector('[data-shipping-progress-track]');
    const progressFill = offer?.querySelector('[data-shipping-progress-fill]');
    const icon = offer?.querySelector('[data-shipping-icon]');
    if (!(offer instanceof HTMLElement) || threshold <= 0) return;

    const safeTotal = Math.max(0, cartTotal);
    const remaining = Math.max(0, threshold - safeTotal);
    const progress = Math.min(1, safeTotal / threshold);
    const wasUnlocked = offer.dataset.freeShippingUnlocked === 'true';
    const isUnlocked = remaining === 0;
    const lockedTemplate = this.dataset.freeShippingLockedText || 'Add [amount] more to unlock free shipping';
    const unlockedText = this.dataset.freeShippingUnlockedText || 'Free shipping unlocked';

    this.dataset.cartTotal = String(safeTotal);
    offer.dataset.freeShippingUnlocked = isUnlocked ? 'true' : 'false';

    if (message) {
      message.textContent = isUnlocked
        ? unlockedText
        : lockedTemplate.replace('[amount]', this.#formatMoney(remaining));
    }

    if (progressFill instanceof HTMLElement) {
      progressFill.style.setProperty('--shipping-progress', String(progress));
    }

    if (progressTrack) {
      progressTrack.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      progressTrack.setAttribute(
        'aria-valuetext',
        isUnlocked ? unlockedText : lockedTemplate.replace('[amount]', this.#formatMoney(remaining))
      );
    }

    if (icon) icon.textContent = isUnlocked ? '✓' : '₹';

    if (celebrate && isUnlocked && !wasUnlocked) {
      offer.dataset.shippingCelebrate = 'true';
      if (this.#shippingCelebrationTimeout) clearTimeout(this.#shippingCelebrationTimeout);
      this.#shippingCelebrationTimeout = window.setTimeout(() => {
        delete offer.dataset.shippingCelebrate;
      }, 900);
    }

    this.#syncStickyBarHeight();
  }

  /**
   * Formats cart amounts using the active storefront currency.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'INR';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (_error) {
      return `₹${Math.ceil(cents / 100)}`;
    }
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles the custom sticky selector launcher while preserving the hidden native select.
   * @param {MouseEvent} event
   */
  #handleStickySelectorClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-sticky-option-trigger]');
    if (trigger) {
      const selector = trigger.closest('.veda-pdp-sticky__selector');
      if (selector instanceof HTMLElement) {
        event.preventDefault();
        this.#openStickyOptionSheet(selector);
      }
      return;
    }

    const choice = target.closest('[data-sticky-option-choice]');
    if (!(choice instanceof HTMLButtonElement)) return;

    const selector = choice.closest('.veda-pdp-sticky__selector');
    const optionValueId = choice.dataset.optionValueId;
    const optionLabel = choice.dataset.optionLabel || choice.textContent?.trim();
    if (!(selector instanceof HTMLElement) || !optionValueId) return;

    this.#selectStickyOption(selector, optionValueId, optionLabel);
  };

  /**
   * Handles clicks on the viewport-level sticky option sheet.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (target instanceof Element && this.#stickySheet) {
      const sheetChoice = target.closest('[data-sticky-sheet-choice]');
      if (sheetChoice instanceof HTMLButtonElement && this.#stickySheet.contains(sheetChoice)) {
        const optionValueId = sheetChoice.dataset.optionValueId;
        const optionLabel = sheetChoice.dataset.optionLabel || sheetChoice.textContent?.trim();

        if (this.#activeStickySelector && optionValueId) {
          this.#selectStickyOption(this.#activeStickySelector, optionValueId, optionLabel);
        }
        return;
      }

      if (target.closest('[data-sticky-sheet-close]') || target === this.#stickySheet) {
        this.#closeStickySheet();
        return;
      }

      if (this.#stickySheet.contains(target)) return;
    }

    if (target instanceof Node && this.contains(target)) return;
    this.#closeStickySheet();
  };

  /**
   * Closes the viewport-level sticky option sheet with Escape.
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#stickySheet) return;
    event.preventDefault();
    this.#closeStickySheet();
  };

  /**
   * Opens the sticky variant selector as a body-level full-screen sheet.
   * @param {HTMLElement} selector
   */
  #openStickyOptionSheet(selector) {
    const choices = Array.from(selector.querySelectorAll('[data-sticky-option-choice]')).filter(
      (choice) => choice instanceof HTMLButtonElement
    );
    if (!choices.length) return;

    this.#closeStickySheet(false);

    const optionLabelElement = selector.querySelector('[id]');
    const selectedLabelElement = selector.querySelector('[data-sticky-selected-label]');
    const optionName = optionLabelElement?.textContent?.trim() || 'Option';
    const selectedLabel = selectedLabelElement?.textContent?.trim() || '';

    this.#activeStickySelector = selector;
    this.#previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    selector.dataset.sheetOpen = 'true';

    const sheet = document.createElement('div');
    sheet.className = 'veda-pdp-sticky__sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', `Select ${optionName}`);

    const panel = document.createElement('div');
    panel.className = 'veda-pdp-sticky__sheet-panel';

    const header = document.createElement('div');
    header.className = 'veda-pdp-sticky__sheet-header';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'veda-pdp-sticky__sheet-close';
    closeButton.dataset.stickySheetClose = 'true';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.innerHTML =
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    const title = document.createElement('h2');
    title.className = 'veda-pdp-sticky__sheet-heading';
    title.textContent = `Select ${optionName}`;

    if (selectedLabel) {
      const subtitle = document.createElement('span');
      subtitle.className = 'veda-pdp-sticky__sheet-subtitle';
      subtitle.textContent = selectedLabel;
      title.appendChild(subtitle);
    }

    const spacer = document.createElement('span');
    spacer.setAttribute('aria-hidden', 'true');

    const options = document.createElement('div');
    options.className = 'veda-pdp-sticky__sheet-options';
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', optionName);

    choices.forEach((sourceChoice) => {
      if (!(sourceChoice instanceof HTMLButtonElement)) return;

      const choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'veda-pdp-sticky__sheet-choice';
      choice.dataset.stickySheetChoice = 'true';
      choice.dataset.optionValueId = sourceChoice.dataset.optionValueId || '';
      choice.dataset.optionLabel = sourceChoice.dataset.optionLabel || sourceChoice.textContent?.trim() || '';
      choice.setAttribute('role', 'option');
      choice.setAttribute('aria-selected', sourceChoice.getAttribute('aria-selected') || 'false');
      choice.disabled = sourceChoice.disabled;

      const text = document.createElement('span');
      text.textContent = sourceChoice.querySelector('span')?.textContent?.trim() || choice.dataset.optionLabel || '';

      const sourceStatus = sourceChoice.querySelector('small');
      if (sourceStatus) {
        const status = document.createElement('small');
        status.textContent = sourceStatus.textContent?.trim() || '';
        text.appendChild(document.createTextNode(' '));
        text.appendChild(status);
      }

      const check = document.createElement('span');
      check.className = 'veda-pdp-sticky__sheet-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';

      choice.append(text, check);
      options.appendChild(choice);
    });

    const foot = document.createElement('div');
    foot.className = 'veda-pdp-sticky__sheet-foot';
    foot.textContent = 'Choose your preferred variant. Price updates instantly.';

    header.append(closeButton, title, spacer);
    panel.append(header, options, foot);
    sheet.appendChild(panel);

    this.#stickySheet = sheet;
    document.body.appendChild(sheet);
    document.body.classList.add('veda-sticky-sheet-open');

    requestAnimationFrame(() => {
      const selectedChoice =
        sheet.querySelector('.veda-pdp-sticky__sheet-choice[aria-selected="true"]') ??
        sheet.querySelector('.veda-pdp-sticky__sheet-choice:not(:disabled)');

      if (selectedChoice instanceof HTMLElement) selectedChoice.focus({ preventScroll: true });
    });
  }

  /**
   * Applies a sticky option selection and delegates the actual variant update.
   * @param {HTMLElement} selector
   * @param {string} optionValueId
   * @param {string | undefined | null} optionLabel
   */
  #selectStickyOption(selector, optionValueId, optionLabel) {
    const select = selector.querySelector('.veda-pdp-sticky__select');
    const label = selector.querySelector('[data-sticky-selected-label]');

    if (!(select instanceof HTMLSelectElement) || !optionValueId) return;

    select.value = optionValueId;
    if (label instanceof HTMLElement && optionLabel) {
      label.textContent = optionLabel.replace(/\s+Sold out$/i, '').trim();
    }

    selector.querySelectorAll('[data-sticky-option-choice]').forEach((element) => {
      element.setAttribute('aria-selected', element.dataset.optionValueId === optionValueId ? 'true' : 'false');
    });

    this.#stickySheet?.querySelectorAll('[data-sticky-sheet-choice]').forEach((element) => {
      element.setAttribute('aria-selected', element.getAttribute('data-option-value-id') === optionValueId ? 'true' : 'false');
    });

    this.#closeStickySheet();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Closes the sticky option sheet.
   * @param {boolean} restoreFocus
   */
  #closeStickySheet(restoreFocus = true) {
    if (this.#activeStickySelector) {
      delete this.#activeStickySelector.dataset.sheetOpen;
    }

    if (this.#stickySheet) {
      this.#stickySheet.remove();
    }

    document.body.classList.remove('veda-sticky-sheet-open');

    if (restoreFocus && this.#previousFocus && document.contains(this.#previousFocus)) {
      this.#previousFocus.focus({ preventScroll: true });
    }

    this.#stickySheet = null;
    this.#activeStickySelector = null;
    this.#previousFocus = null;
  }

  /**
   * Mirrors mobile sticky option dropdowns into the real product variant picker.
   * Keeping the canonical picker in charge preserves Shopify section re-rendering,
   * price/media updates, and third-party cart integrations.
   * @param {Event} event
   */
  #handleStickyOptionChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('.veda-pdp-sticky__select')) return;

    const productId = this.dataset.productId;
    const optionValueId = select.value;
    const optionIndex = select.dataset.stickyOptionIndex;
    if (!productId || !optionValueId || optionIndex == null) return;

    const sectionElement = this.closest('.shopify-section');
    const variantPicker =
      sectionElement?.querySelector(`variant-picker[data-product-id="${productId}"]`) ??
      document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantPicker) return;

    const radioTarget = Array.from(variantPicker.querySelectorAll('input[data-option-value-id]')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      return input.dataset.optionValueId === optionValueId && input.dataset.fieldsetIndex === optionIndex;
    });

    if (radioTarget instanceof HTMLInputElement) {
      radioTarget.click();
      return;
    }

    const nativeSelect = Array.from(variantPicker.querySelectorAll('select')).find((variantSelect) => {
      if (!(variantSelect instanceof HTMLSelectElement)) return false;
      return Array.from(variantSelect.options).some((option) => option.dataset.optionValueId === optionValueId);
    });

    if (!(nativeSelect instanceof HTMLSelectElement)) return;

    const matchingOption = Array.from(nativeSelect.options).find(
      (option) => option.dataset.optionValueId === optionValueId
    );
    if (!matchingOption) return;

    nativeSelect.value = matchingOption.value;
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#setFloatingWidgetsLift(true);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    if (this.#shouldAlwaysStickOnMobile()) {
      this.#showStickyBar();
      return;
    }

    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#setFloatingWidgetsLift(false);
  }

  /**
   * Re-scans when app launchers are injected or rebuilt after the PDP is ready.
   */
  #setupFloatingWidgetsObserver() {
    if (!('MutationObserver' in window)) return;

    this.#floatingWidgetsObserver = new MutationObserver(() => {
      this.#observeVerifastState();
      this.#queueFloatingWidgetsScan();
    });
    this.#floatingWidgetsObserver.observe(document.body, { childList: true, subtree: true });
    this.#observeVerifastState();
  }

  /**
   * Watches only Verifast's explicit open/closed class instead of every style
   * mutation across the PDP.
   */
  #observeVerifastState() {
    const frame = document.querySelector('#verifast-react-chatbot-iframe');
    if (!(frame instanceof HTMLElement) || frame === this.#observedVerifastFrame) return;

    this.#verifastStateObserver?.disconnect();
    this.#observedVerifastFrame = frame;
    this.#verifastStateObserver = new MutationObserver(() => this.#queueFloatingWidgetsScan());
    this.#verifastStateObserver.observe(frame, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Batches launcher positioning into one update per animation frame.
   */
  #queueFloatingWidgetsScan() {
    if (!document.body.classList.contains('veda-sticky-atc-visible') || this.#floatingWidgetsScanFrame) return;

    this.#floatingWidgetsScanFrame = requestAnimationFrame(() => {
      this.#floatingWidgetsScanFrame = null;
      this.#setFloatingWidgetsLift(true);
    });
  }

  /**
   * Moves third-party floating launchers out of the sticky ATC collision zone on mobile.
   * This only adds/removes classes and does not change app-owned markup or settings.
   * @param {boolean} isLifted
   */
  #setFloatingWidgetsLift(isLifted) {
    document.body.classList.toggle('veda-sticky-atc-visible', isLifted);

    if (!window.matchMedia('(max-width: 749px)').matches) {
      document.querySelectorAll('.veda-floating-widget--lifted, .veda-floating-widget--expanded').forEach((element) => {
        element.classList.remove('veda-floating-widget--lifted', 'veda-floating-widget--expanded');
      });
      return;
    }

    const selectors = [
      '.smile-launcher-frame-container',
      '.smile-launcher',
      '#verifast-react-chatbot-iframe',
      'iframe[src*="verifast"]',
    ].join(',');

    document.querySelectorAll(selectors).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.closest('veda-pdp-sticky') || element.closest('#header-group')) return;

      let target = null;
      let candidate = element;
      let depth = 0;

      while (candidate && depth < 8) {
        const style = window.getComputedStyle(candidate);
        if (style.position === 'fixed') {
          target = candidate;
          break;
        }
        candidate = candidate.parentElement;
        depth += 1;
      }

      if (!target) return;

      const isVerifast = element.matches(
        '#verifast-react-chatbot-iframe, iframe[src*="verifast"]'
      );
      const isExpandedVerifast =
        isVerifast &&
        (element.classList.contains('verifast-fullscreen') ||
          element.classList.contains('verifast-fullscreen-all'));

      if (isLifted && isExpandedVerifast) {
        target.classList.remove('veda-floating-widget--lifted');
        target.classList.add('veda-floating-widget--expanded');
        return;
      }

      target.classList.remove('veda-floating-widget--expanded');
      target.classList.toggle('veda-floating-widget--lifted', isLifted);
    });
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Finds the primary product form add-to-cart button.
   */
  #cacheTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Keeps the PDP mobile add-to-cart bar persistent.
   * @returns {boolean}
   */
  #shouldAlwaysStickOnMobile() {
    return document.body.classList.contains('template-product') && window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Sync sticky state after mobile/desktop viewport changes.
   */
  #handleViewportChange = () => {
    if (this.#shouldAlwaysStickOnMobile()) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
    }
    this.#syncStickyBarHeight();
  };

  /**
   * Keeps page-bottom spacing equal to the sticky bar's real rendered height.
   */
  #setupStickyHeightObserver() {
    this.#syncStickyBarHeight();
    if (!('ResizeObserver' in window)) return;

    this.#stickyResizeObserver = new ResizeObserver(() => this.#syncStickyBarHeight());
    this.#stickyResizeObserver.observe(this.refs.stickyBar);
  }

  #syncStickyBarHeight() {
    if (!this.#shouldAlwaysStickOnMobile()) {
      document.body.style.removeProperty('--veda-sticky-atc-mobile-height');
      return;
    }

    const height = Math.ceil(this.refs.stickyBar.getBoundingClientRect().height);
    if (height > 0) {
      document.body.style.setProperty('--veda-sticky-atc-mobile-height', `${height}px`);
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('veda-pdp-sticky')) {
  customElements.define('veda-pdp-sticky', StickyAddToCartComponent);
}
